import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { APP_LANDING_SEO } from '../../../seo/pageSeoMeta';
import Hero from './components/Hero';
import Features from './components/Features';
import Screenshots from './components/Screenshots';
import HowItWorks from './components/HowItWorks';
import SupportedRegions from './components/SupportedRegions';
import CTA from './components/CTA';
import styled, { createGlobalStyle } from 'styled-components';

const GlobalFontStyle = createGlobalStyle`
  /* Font will inherit from body default */
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: white;
`;

const AppLandingPage = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <>
      <Helmet prioritizeSeoTags>
        <html lang={i18n.language} />
        <title>{APP_LANDING_SEO.title}</title>
        <meta name="description" content={APP_LANDING_SEO.description} />
        <meta name="keywords" content="train travel europe, rail journey planner, train delay tracker, connection alerts, european trains, railway app, train punctuality, DB, Deutsche Bahn, Trenitalia, SNCF, ÖBB, SBB, Zugfinder alternative, Omio alternative, Trainline alternative, best train app europe, train delay predictions, smart train travel, real-time train tracking, train connection planner, european railway app, train reliability scores, live train updates" />
        <meta name="author" content="chuuchuu" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://chuuchuu.com/app" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={APP_LANDING_SEO.title} />
        <meta property="og:description" content={APP_LANDING_SEO.description} />
        <meta property="og:url" content={APP_LANDING_SEO.url} />
        <meta property="og:site_name" content="chuuchuu" />
        <meta property="og:image" content={APP_LANDING_SEO.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="chuuchuu - Smart train travel app for Europe" />
        <meta property="og:locale" content={i18n.language === 'de' ? 'de_DE' : 'en_US'} />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:locale:alternate" content="de_DE" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={APP_LANDING_SEO.title} />
        <meta name="twitter:description" content={APP_LANDING_SEO.description} />
        <meta name="twitter:image" content={APP_LANDING_SEO.image} />
        <meta name="twitter:image:alt" content="chuuchuu - Smart train travel app for Europe" />

        {/* Mobile */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="chuuchuu" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />

        {/* Theme */}
        <meta name="theme-color" content="#9333ea" />
        <meta name="msapplication-TileColor" content="#9333ea" />

        {/* Structured Data - Mobile Application */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "chuuchuu",
            "description": "Smart travel assistant for train travel in Europe with real-time delay predictions, reliability scores, and live journey tracking. A better alternative to Zugfinder, Omio, and Trainline.",
            "applicationCategory": "TravelApplication",
            "operatingSystem": "iOS",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "ratingCount": "1"
            },
            "author": {
              "@type": "Organization",
              "name": "chuuchuu",
              "url": "https://chuuchuu.com"
            },
            "countriesSupported": ["DE", "IT", "FR", "AT", "CH", "BE", "NL"],
            "featureList": [
              "Real-time delay predictions",
              "Journey reliability scores",
              "Live journey tracking",
              "Smart connection alerts",
              "Historical delay statistics",
              "Transfer time analysis"
            ]
          })}
        </script>

        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "chuuchuu",
            "description": "Smart train travel app for Europe with delay predictions and live tracking. Better than Zugfinder, Omio, and Trainline.",
            "applicationCategory": "TravelApplication",
            "operatingSystem": "iOS",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "ratingCount": "1"
            },
            "author": {
              "@type": "Organization",
              "name": "chuuchuu",
              "url": "https://chuuchuu.com"
            }
          })}
        </script>

        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "chuuchuu",
            "url": "https://chuuchuu.com",
            "logo": "https://chuuchuu.com/images/color=key@2x.png",
            "description": "Smart train travel app for Europe providing real-time delay predictions and journey tracking",
            "sameAs": [
              "https://instagram.com/chuuchuu.app",
              "https://bsky.app/profile/chuuchuuapp.bsky.social",
              "https://mastodon.social/@chuuchuu"
            ]
          })}
        </script>
      </Helmet>
      <GlobalFontStyle />
      <PageContainer data-app-landing-page>
        <Hero />
        <Features part="more" />
        <Screenshots />
        <Features part="intro" />
        <HowItWorks />
        <SupportedRegions />
        <CTA />
      </PageContainer>
    </>
  );
};

export default AppLandingPage;

